import React, { useCallback, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, AlertTriangleIcon, CheckIcon, FacebookIcon, InstagramIcon, TwitterIcon, UploadCloudIcon, FileTextIcon, LayoutTemplateIcon, PaletteIcon, Share2Icon, TagsIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import type { ChannelKey, Localized } from '../types/notice';
import { CHANNEL_ICONS, CHANNEL_LABELS } from '../data/channels';
import { IconTooltip } from './IconTooltip';
import { CategoryFormDialog } from './notice/CategoryFormDialog';
import { ConfirmDeleteDialog } from './notice/ConfirmDeleteDialog';
import { CATEGORIES, getCategoryIcon, getCategoryLabel, getTemplateCategory, getTemplateChannels, type NoticeTemplate } from '../data/communications';
export type ConfigSection = 'categories' | 'reseaux' | 'modeles' | 'identite';
type Section = ConfigSection;
export interface Categorie {
  id: string;
  titre: Localized;
  description: Localized;
  urgence: boolean;
  defaut: boolean;
  statut: 'active' | 'brouillon' | 'inactive';
}
export const MOCK_CATEGORIES: Categorie[] = [{
  id: '1',
  titre: {
    fr: 'Urgence',
    en: 'Emergency'
  },
  description: {
    fr: 'Avis critiques nécessitant une action immédiate',
    en: 'Critical notices requiring immediate action'
  },
  urgence: true,
  defaut: false,
  statut: 'active'
}, {
  id: '2',
  titre: {
    fr: 'Eau potable',
    en: 'Drinking water'
  },
  description: {
    fr: "Avis d'ébullition et coupures d'eau",
    en: 'Boil advisories and water shutdowns'
  },
  urgence: true,
  defaut: false,
  statut: 'active'
}, {
  id: '3',
  titre: {
    fr: 'Travaux',
    en: 'Public works'
  },
  description: {
    fr: 'Fermetures de rues et chantiers',
    en: 'Street closures and work sites'
  },
  urgence: false,
  defaut: false,
  statut: 'active'
}, {
  id: '4',
  titre: {
    fr: 'Information générale',
    en: 'General information'
  },
  description: {
    fr: 'Nouvelles et mises à jour de la ville',
    en: 'City news and updates'
  },
  urgence: false,
  defaut: true,
  statut: 'active'
}];
interface CommunicationsConfigurerProps {
  categories: Categorie[];
  onCategoriesChange: (categories: Categorie[]) => void;
  templates: NoticeTemplate[];
  onCreateTemplate: () => void;
  onEditTemplate: (template: NoticeTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
  /** Section shown first — e.g. back on "Modèles" after editing a template. */
  initialSection?: Section;
}
export function CommunicationsConfigurer({
  categories,
  onCategoriesChange,
  templates,
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate,
  initialSection = 'categories'
}: CommunicationsConfigurerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [activeSection, setActiveSection] = useState<Section>(initialSection);
  /** Template awaiting a delete confirmation, shown inline on its row. */
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  /** Category form: closed, or open on a category (null = creating). */
  const [categoryForm, setCategoryForm] = useState<{
    open: false;
  } | {
    open: true;
    category: Categorie | null;
  }>({
    open: false
  });
  const [categoryToDelete, setCategoryToDelete] = useState<Categorie | null>(null);
  const closeCategoryForm = useCallback(() => setCategoryForm({
    open: false
  }), []);
  const cancelCategoryDelete = useCallback(() => setCategoryToDelete(null), []);
  const saveCategory = (saved: Categorie) => {
    const exists = categories.some(c => c.id === saved.id);
    const next = exists ? categories.map(c => c.id === saved.id ? saved : c) : [...categories, saved];
    // Only one category can be the default one.
    onCategoriesChange(saved.defaut ? next.map(c => c.id === saved.id ? c : {
      ...c,
      defaut: false
    }) : next);
    setCategoryForm({
      open: false
    });
  };
  const confirmCategoryDelete = () => {
    if (!categoryToDelete) return;
    onCategoriesChange(categories.filter(c => c.id !== categoryToDelete.id));
    setCategoryToDelete(null);
  };
  const statutLabel = (statut: Categorie['statut']) => statut === 'active' ? t('Active', 'Active') : statut === 'brouillon' ? t('Brouillon', 'Draft') : t('Inactive', 'Inactive');
  const renderCategories = () => <div className="space-y-6" data-id="element-1641" data-mp="tl6hj607biij">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1642" data-mp="tkwnn7c1cr7k">
        <div data-id="element-1643" data-mp="yhwxzufx4g4h">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1644" data-mp="yipxqbggm5d4">
            {t("Catégories d'avis", 'Notice categories')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1645" data-mp="ol1zvcnk4hqq">
            {t("Gérez les catégories disponibles lors de la création d'un avis.", 'Manage the categories available when creating a notice.')}
          </p>
        </div>
        <button type="button" onClick={() => setCategoryForm({
        open: true,
        category: null
      })} className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-1646" data-mp="39cklr62scpry">
          <PlusIcon className="w-4 h-4" data-id="element-1647" data-mp="20db47l8al5ql" /> {t('Nouvelle catégorie', 'New category')}
        </button>
      </div>

      {categories.length === 0 ? <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center" data-id="element-1648" data-mp="1wawld14mi5zb">
          <div className="mx-auto w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-[#4F46E5] mb-3" data-id="element-1649" data-mp="18ksemkw51q43">
            <TagsIcon className="w-6 h-6" data-id="element-1650" data-mp="1hg04y0mnucb5" />
          </div>
          <p className="font-semibold text-slate-900" data-id="element-1651" data-mp="1hhkybmx8np7f">
            {t('Aucune catégorie', 'No categories')}
          </p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1652" data-mp="oz5ueblee1nc">
            {t('Créez une première catégorie pour classer vos avis.', 'Create a first category to organize your notices.')}
          </p>
        </div> : <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-1653" data-mp="2k1ssbt48qtz2">
        <div className="overflow-x-auto" data-id="element-1654" data-mp="2ozuc6g9hqxbl">
          <table className="w-full text-left border-collapse" data-id="element-1655" data-mp="2ts5a5dko891i">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-1656" data-mp="2tsne6wdkrrjz">
              <tr data-id="element-1657" data-mp="2yq6tlzm0eadi">
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-1658" data-mp="2yqozzp2dy4z5">
                  {t('Titre et description', 'Title & description')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-1659" data-mp="263cd1dsbbv5b">
                  {t('Urgence', 'Emergency')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-center" data-id="element-1660" data-mp="2g1s3rfnfh2ea">
                  {t('Par défaut', 'Default')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide" data-id="element-1661" data-mp="3rfe1hp7cscbf">
                  {t('Statut', 'Status')}
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wide text-right" data-id="element-1662" data-mp="1fud7at0eb2m8">
                  {t('Actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-1663" data-mp="113xlpd42hsnj">
              {categories.map(cat => <tr key={cat.id} onDoubleClick={() => setCategoryForm({
              open: true,
              category: cat
            })} className="hover:bg-slate-50 transition-colors" data-id="element-1664" data-mp="w4bb7bfmiqif">
                  <td className="px-4 py-3" data-id="element-1665" data-mp="n33euywo3zqz">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-1666" data-mp="n5zc0l0oyked">
                      {cat.titre[lang]}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5" data-id="element-1667" data-mp="2hiflrvsp4gnz">
                      {cat.description[lang]}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1668" data-mp="2ch02o9a2wk88">
                    {cat.urgence && <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-red-100 text-red-600" data-id="element-1669" data-mp="1lftcf8jbz1k3">
                        <AlertTriangleIcon className="w-3 h-3" data-id="element-1670" data-mp="1bnxcdrxop1d8" />
                      </span>}
                  </td>
                  <td className="px-4 py-3 text-center" data-id="element-1671" data-mp="1glfp7teto0qi">
                    {cat.defaut && <span className="inline-flex items-center justify-center w-5 h-5 rounded bg-emerald-100 text-emerald-600" data-id="element-1672" data-mp="12lxyh8hyudei">
                        <CheckIcon className="w-3 h-3" data-id="element-1673" data-mp="17ndegm5adtq1" />
                      </span>}
                  </td>
                  <td className="px-4 py-3" data-id="element-1674" data-mp="xnw9y81rtlvn">
                    <span className={`inline-flex px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border ${cat.statut === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : cat.statut === 'brouillon' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`} data-id="element-1675" data-mp="xonzb14ryndh">
                      {statutLabel(cat.statut)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-1676" data-mp="q4n2wlgqlz9r">
                    <div className="flex items-center justify-end gap-2" data-id="element-1677" data-mp="q66kqgi1kidn">
                      <button type="button" onClick={() => setCategoryForm({
                    open: true,
                    category: cat
                  })} className="p-1.5 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded transition-colors" aria-label={`${t('Modifier', 'Edit')} ${cat.titre[lang]}`} data-id="element-1678" data-mp="lc2ey0kiige5">
                        <PencilIcon className="w-4 h-4" data-id="element-1679" data-mp="145fyzamot2ez" />
                      </button>
                      <button type="button" onClick={() => setCategoryToDelete(cat)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" aria-label={`${t('Supprimer', 'Delete')} ${cat.titre[lang]}`} data-id="element-1680" data-mp="3avnkqw32aosi">
                        <TrashIcon className="w-4 h-4" data-id="element-1681" data-mp="zqek7xafoqu5" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>}

      <CategoryFormDialog open={categoryForm.open} category={categoryForm.open ? categoryForm.category : null} onClose={closeCategoryForm} onSave={saveCategory} data-id="element-1682" data-mp="1se0kowod7fcs" />

      <ConfirmDeleteDialog open={Boolean(categoryToDelete)} title={t('Supprimer cette catégorie ?', 'Delete this category?')} message={categoryToDelete && <>
              {t('La catégorie ', 'The category ')}
              <span className="font-semibold text-slate-700" data-id="element-1684" data-mp="26f37xcr3ivpa">
                « {categoryToDelete.titre[lang]} »
              </span>
              {t(' ne sera plus proposée à la création d’un avis. Les avis déjà envoyés ne sont pas modifiés.', ' will no longer be offered when creating a notice. Notices already sent are not affected.')}
            </>} onCancel={cancelCategoryDelete} onConfirm={confirmCategoryDelete} data-id="element-1683" data-mp="1inxu134pudhm" />
    </div>;
  const renderReseaux = () => <div className="space-y-6" data-id="element-1685" data-mp="but0fpmjzva7">
      <div data-id="element-1686" data-mp="gu5twt7xk0lr">
        <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1687" data-mp="gtmd5fu0ni9b">
          {t('Réseaux sociaux', 'Social media')}
        </h3>
        <p className="text-sm text-slate-500 mt-1" data-id="element-1688" data-mp="llxd2iorb41v">
          {t('Connectez les comptes officiels pour la publication automatisée.', 'Connect the official accounts for automated publishing.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1689" data-mp="ujpx205fazvl">
        {/* Facebook */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1690" data-mp="zijaxtomgswa">
          <div className="flex items-start justify-between mb-4" data-id="element-1691" data-mp="2vlpf33wqs90a">
            <div className="flex items-center gap-3" data-id="element-1692" data-mp="2qq084fie7pty">
              <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-1693" data-mp="35dbkukqb9fvn">
                <FacebookIcon className="w-5 h-5" data-id="element-1694" data-mp="30md1fca101o6" />
              </div>
              <div data-id="element-1695" data-mp="30l21xonm5g2v">
                <h4 className="font-semibold text-slate-900" data-id="element-1696" data-mp="3edqk6o040vi5">Facebook</h4>
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1697" data-mp="3eg4f8wusi7pp">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1698" data-mp="3af0v3y5plpio" />{' '}
                  {t('Connecté', 'Connected')}
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-slate-500 hover:text-slate-700" data-id="element-1699" data-mp="3jb9wvjh5t07z">
              {t('Déconnecter', 'Disconnect')}
            </button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 mt-auto" data-id="element-1700" data-mp="1p0n005qd2ket">
            <span className="font-medium text-slate-900" data-id="element-1701" data-mp="4ic91qymjq2k">
              {t('Page :', 'Page:')}
            </span>{' '}
            Ville de Windsor
          </div>
        </div>

        {/* X (Twitter) */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1702" data-mp="3qw4173fw6jvf">
          <div className="flex items-start justify-between mb-4" data-id="element-1703" data-mp="3vunop8a4w3pw">
            <div className="flex items-center gap-3" data-id="element-1704" data-mp="3hvo0u2ug3sus">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-900" data-id="element-1705" data-mp="3huw4uqwxrt3z">
                <TwitterIcon className="w-5 h-5" data-id="element-1706" data-mp="3msxnxbud3sfg" />
              </div>
              <div data-id="element-1707" data-mp="37zkpq7hm8jvp">
                <h4 className="font-semibold text-slate-900" data-id="element-1708" data-mp="38049ltqtticd">X (Twitter)</h4>
                <p className="text-xs text-emerald-600 font-medium flex items-center gap-1 mt-0.5" data-id="element-1709" data-mp="3cxmglela9nhq">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-1710" data-mp="1676879izhhft" />{' '}
                  {t('Connecté', 'Connected')}
                </p>
              </div>
            </div>
            <button className="text-xs font-semibold text-slate-500 hover:text-slate-700" data-id="element-1711" data-mp="377ky0ubgnjpt">
              {t('Déconnecter', 'Disconnect')}
            </button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 mt-auto" data-id="element-1712" data-mp="2y7obozcjmwxx">
            <span className="font-medium text-slate-900" data-id="element-1713" data-mp="2t9d2mqu2oqgr">
              {t('Compte :', 'Account:')}
            </span>{' '}
            @villewindsor
          </div>
        </div>

        {/* Instagram */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col shadow-sm" data-id="element-1714" data-mp="2jirxslnt2ppe">
          <div className="flex items-start justify-between mb-4" data-id="element-1715" data-mp="2fh6o196kirjf">
            <div className="flex items-center gap-3" data-id="element-1716" data-mp="2ffc2bsnh2vea">
              <div className="w-10 h-10 rounded-full bg-pink-50 flex items-center justify-center text-pink-600" data-id="element-1717" data-mp="2ajocdb161y7q">
                <InstagramIcon className="w-5 h-5" data-id="element-1718" data-mp="25j0now0ettgo" />
              </div>
              <div data-id="element-1719" data-mp="20k5wp6sagylh">
                <h4 className="font-semibold text-slate-900" data-id="element-1720" data-mp="20lqpaqv1m7gp">Instagram</h4>
                <p className="text-xs text-slate-400 font-medium flex items-center gap-1 mt-0.5" data-id="element-1721" data-mp="1qhac658y74gl">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300" data-id="element-1722" data-mp="1vesnu3ecnxsa" />{' '}
                  {t('Non connecté', 'Not connected')}
                </p>
              </div>
            </div>
          </div>
          <button className="mt-auto w-full py-2.5 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors" data-id="element-1723" data-mp="25e1ascassc55">
            {t('Connecter le compte', 'Connect the account')}
          </button>
        </div>
      </div>
    </div>;
  const renderModeles = () => <div className="space-y-6" data-id="element-1724" data-mp="1hnxdemo7d8ml">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1725" data-mp="1hmvxo4w8rxyj">
        <div data-id="element-1726" data-mp="1mfyrme4xl9f9">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1727" data-mp="1mgglx5819dkj">
            {t("Modèles d'avis", 'Notice templates')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1728" data-mp="oxozj70p9adt">
            {t("Configurez des modèles prédéfinis pour accélérer la création d'avis.", 'Set up predefined templates to speed up notice creation.')}
          </p>
        </div>
        <button type="button" onClick={onCreateTemplate} className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors flex items-center gap-2" data-id="element-1729" data-mp="gyajwzhnhua3">
          <PlusIcon className="w-4 h-4" data-id="element-1730" data-mp="2z2l47pego6f" /> {t('Nouveau modèle', 'New template')}
        </button>
      </div>

      {templates.length === 0 ? <div className="bg-white rounded-xl border border-dashed border-slate-300 p-10 text-center" data-id="element-1731" data-mp="3u6ezkeywenin">
          <div className="mx-auto w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center text-[#4F46E5] mb-3" data-id="element-1732" data-mp="cy05cyfslvjm">
            <LayoutTemplateIcon className="w-6 h-6" data-id="element-1733" data-mp="7wuaov2kny7g" />
          </div>
          <p className="font-semibold text-slate-900" data-id="element-1734" data-mp="1obcenoubn6d6">
            {t('Aucun modèle', 'No templates')}
          </p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1735" data-mp="1jcso1b4s96eh">
            {t('Créez un modèle ici ou enregistrez un avis comme modèle depuis le formulaire.', 'Create a template here, or save a notice as a template from the composer.')}
          </p>
        </div> : <ul className="grid grid-cols-1 gap-3" data-id="element-1736" data-mp="1hxyca967uac9">
          {templates.map(tpl => {
        const confirming = confirmDeleteId === tpl.id;
        const categoryId = getTemplateCategory(tpl);
        const CategoryIcon = getCategoryIcon(categoryId);
        const urgent = Boolean(CATEGORIES.find(c => c.value === categoryId)?.urgent);
        const channels = getTemplateChannels(tpl) as ChannelKey[];
        return <li key={tpl.id} onClick={() => !confirming && onEditTemplate(tpl)} className="bg-white rounded-xl border border-slate-200 px-5 py-4 flex items-center justify-between gap-4 hover:border-[#4F46E5]/50 hover:shadow-sm transition-all group cursor-pointer" data-id="element-1737" data-mp="24va0i8ckoxd0">
                <div className="flex items-center gap-4 min-w-0" data-id="element-1738" data-mp="28ocfe5kedc0t">
                  <IconTooltip label={getCategoryLabel(categoryId)[lang]} data-id="element-1739" data-mp="23urd41brtvvt">
                    <span className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${urgent ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-[#4F46E5]'}`} data-id="element-1740" data-mp="1yvwsctu8tx92">
                      <CategoryIcon className="w-5 h-5" aria-hidden="true" data-id="element-1741" data-mp="2mm0z3a2qad4a" />
                    </span>
                  </IconTooltip>
                  {/* Channels: up to 3 per row, 2 rows, vertically centered */}
                  <div className="grid grid-cols-3 gap-1 w-[68px] flex-shrink-0 content-center" data-id="element-1742" data-mp="2co39oj94uaa3">
                    {channels.map(c => {
                const Icon = CHANNEL_ICONS[c];
                return <IconTooltip key={c} label={CHANNEL_LABELS[c][lang]} data-id="element-1743" data-mp="117mqqfyddbih">
                          <span className="w-5 h-5 flex items-center justify-center text-slate-400 hover:text-[#4F46E5] transition-colors" data-id="element-1744" data-mp="3cwjhzcl090kx">
                            <Icon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-1745" data-mp="3hvnofvgjok7k" />
                          </span>
                        </IconTooltip>;
              })}
                  </div>
                  <div className="min-w-0" data-id="element-1746" data-mp="3rn0i8slwlkg8">
                    <div className="flex items-center gap-2 min-w-0" data-id="element-1747" data-mp="f5yz0bofykh">
                      <h4 className="font-semibold text-slate-900 truncate" data-id="element-1748" data-mp="4g7wncpy42kc">
                        {tpl.name[lang]}
                      </h4>
                      {tpl.custom && <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wide text-[#4F46E5] bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-1749" data-mp="9d86lypx1pgq">
                          {t('Équipe', 'Team')}
                        </span>}
                    </div>
                    <p className="text-sm text-slate-500 truncate mt-0.5" data-id="element-1750" data-mp="2g60tu82ofprh">
                      {tpl.description[lang]}
                    </p>
                  </div>
                </div>

                {confirming ? <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()} data-id="element-1751" data-mp="t157nqywc5my">
                    <span className="text-sm text-slate-600 hidden sm:inline" data-id="element-1752" data-mp="11x4cf4qvw8x2">
                      {t('Supprimer ce modèle ?', 'Delete this template?')}
                    </span>
                    <button type="button" onClick={() => setConfirmDeleteId(null)} className="px-3 py-1.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50" data-id="element-1753" data-mp="1bspjvieq21e7">
                      {t('Annuler', 'Cancel')}
                    </button>
                    <button type="button" onClick={() => {
              onDeleteTemplate(tpl.id);
              setConfirmDeleteId(null);
            }} className="px-3 py-1.5 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700" data-id="element-1754" data-mp="3mlsd34f9ys3t">
                      {t('Supprimer', 'Delete')}
                    </button>
                  </div> : <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity" data-id="element-1755" data-mp="1w0uxesxogxi">
                    <button type="button" aria-label={t('Modifier', 'Edit')} onClick={e => {
              e.stopPropagation();
              onEditTemplate(tpl);
            }} className="p-2 text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-1756" data-mp="3t3lprnxrk3po">
                      <PencilIcon className="w-4 h-4" data-id="element-1757" data-mp="28l5tp3f3wl0e" />
                    </button>
                    <button type="button" aria-label={t('Supprimer', 'Delete')} onClick={e => {
              e.stopPropagation();
              setConfirmDeleteId(tpl.id);
            }} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1758" data-mp="2a45pdkj3nnhc">
                      <TrashIcon className="w-4 h-4" data-id="element-1759" data-mp="1rb1xo4pfzkpd" />
                    </button>
                  </div>}
              </li>;
      })}
        </ul>}
    </div>;
  const renderIdentite = () => <div className="space-y-6" data-id="element-1760" data-mp="1mdigox1d0ttq">
      <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-1761" data-mp="1metmf2ed8bnh">
        <div data-id="element-1762" data-mp="18e0mu397iqdi">
          <h3 className="text-lg font-bold text-[#0F172A]" data-id="element-1763" data-mp="18fbrs6ee2467">
            {t('Identité graphique', 'Visual identity')}
          </h3>
          <p className="text-sm text-slate-500 mt-1" data-id="element-1764" data-mp="1dfg91yn4f2xh">
            {t("Personnalisez l'apparence des communications envoyées aux citoyens.", 'Customize how the communications sent to citizens look.')}
          </p>
        </div>
        <button className="px-4 py-2 text-sm font-medium text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors" data-id="element-1765" data-mp="12t1gpcsedm7j">
          {t('Enregistrer les modifications', 'Save changes')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-8 shadow-sm" data-id="element-1766" data-mp="1colc7y31uivr">
        {/* Logo */}
        <div data-id="element-1767" data-mp="17pig5351rmzx">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1768" data-mp="1mg95m4j629zv">
            {t('Logo officiel', 'Official logo')}
          </label>
          <div className="flex items-center gap-6 flex-wrap" data-id="element-1769" data-mp="1hih54m4ghfk2">
            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-200 flex flex-col items-center justify-center bg-slate-50 text-slate-400 hover:border-[#4F46E5] hover:bg-indigo-50 transition-colors cursor-pointer" data-id="element-1770" data-mp="1vgnt9ajls16j">
              <UploadCloudIcon className="w-6 h-6 mb-2" data-id="element-1771" data-mp="3kx9ozlms2fu5" />
              <span className="text-xs font-medium" data-id="element-1772" data-mp="3pww09pj1pm0b">
                {t('Changer le logo', 'Change logo')}
              </span>
            </div>
            <div className="flex-1 min-w-[15rem]" data-id="element-1773" data-mp="3up5p128fcg10">
              <p className="text-sm text-slate-500 mb-2" data-id="element-1774" data-mp="2jtpo9810snu">
                {t("Ce logo sera utilisé dans l'en-tête des courriels et sur le portail citoyen.", 'This logo is used in the email header and on the citizen portal.')}
              </p>
              <p className="text-xs text-slate-400" data-id="element-1775" data-mp="3b04zbbtq428l">
                {t('Format recommandé : PNG transparent, 400x100px max, 2 Mo max.', 'Recommended format: transparent PNG, 400x100px max, 2 MB max.')}
              </p>
            </div>
          </div>
        </div>

        <hr className="border-slate-100" data-id="element-1776" data-mp="v7xvcuqll5qj" />

        {/* Colours */}
        <div data-id="element-1777" data-mp="15co17bp4gu01">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1778" data-mp="15d79e0oz040v">
            {t('Couleurs de la marque', 'Brand colours')}
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1779" data-mp="10ecqyzux0hha">
            <div data-id="element-1780" data-mp="vlt3nb6fe7za">
              <label className="block text-xs text-slate-500 mb-1.5" data-id="element-1781" data-mp="vlb9ck3bq3u0">
                {t('Couleur principale', 'Primary colour')}
              </label>
              <div className="flex items-center gap-3" data-id="element-1782" data-mp="mlwnjxy3qhfu">
                <div className="w-10 h-10 rounded-lg bg-[#4F46E5] border border-slate-200 shadow-sm" data-id="element-1783" data-mp="mnhhpmgi7yd6" />
                <input type="text" defaultValue="#4F46E5" aria-label={t('Couleur principale', 'Primary colour')} className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-1784" data-mp="hls5w4lgjgo1" />
              </div>
            </div>
            <div data-id="element-1785" data-mp="230696kayls2z">
              <label className="block text-xs text-slate-500 mb-1.5" data-id="element-1786" data-mp="231r5ofgu3l3h">
                {t('Couleur secondaire', 'Secondary colour')}
              </label>
              <div className="flex items-center gap-3" data-id="element-1787" data-mp="54hb3js1z6kc">
                <div className="w-10 h-10 rounded-lg bg-[#0F172A] border border-slate-200 shadow-sm" data-id="element-1788" data-mp="5623p3ut4ffk" />
                <input type="text" defaultValue="#0F172A" aria-label={t('Couleur secondaire', 'Secondary colour')} className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-1789" data-mp="3nk1kjvvaqozd" />
              </div>
            </div>
          </div>
        </div>

        <hr className="border-slate-100" data-id="element-1790" data-mp="a2sm3xu33n45" />

        {/* Email footer */}
        <div data-id="element-1791" data-mp="f25fl1fgnsfp">
          <label className="block text-sm font-semibold text-slate-900 mb-3" data-id="element-1792" data-mp="f1m15up0rm6f">
            {t('Pied de page des courriels', 'Email footer')}
          </label>
          <textarea rows={4} aria-label={t('Pied de page des courriels', 'Email footer')} defaultValue={'Ville de Windsor\n11, rue St-Georges, Windsor (Québec) J1S 1J4\nTéléphone : 819 845-7888'} className="w-full border border-slate-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#4F46E5] resize-none" data-id="element-1793" data-mp="2zsw71nctw8s8" />
          <p className="text-xs text-slate-500 mt-2" data-id="element-1794" data-mp="2wb8878tdwca7">
            {t('Ce texte apparaîtra au bas de toutes les communications par courriel.', 'This text appears at the bottom of every email communication.')}
          </p>
        </div>
      </div>
    </div>;
  const nav: {
    id: Section;
    label: string;
    icon: typeof TagsIcon;
  }[] = [{
    id: 'categories',
    label: t('Catégories', 'Categories'),
    icon: TagsIcon
  }, {
    id: 'reseaux',
    label: t('Réseaux sociaux', 'Social media'),
    icon: Share2Icon
  }, {
    id: 'modeles',
    label: t('Modèles', 'Templates'),
    icon: LayoutTemplateIcon
  }, {
    id: 'identite',
    label: t('Identité', 'Identity'),
    icon: PaletteIcon
  }];
  return <div className="flex flex-col lg:flex-row gap-8 font-['Inter']" data-id="element-1795" data-mp="zd9yu6e9o6qm">
      {/* Section switcher */}
      <div className="lg:w-64 flex-shrink-0" data-id="element-1796" data-mp="14arzl9i4rfnj">
        <nav aria-label={t('Sections de configuration', 'Configuration sections')} className="bg-white rounded-xl border border-slate-200 p-2 space-y-1 lg:sticky lg:top-4 shadow-sm" data-id="element-1797" data-mp="14dnwcui8ryae">
          {nav.map(item => {
          const Icon = item.icon;
          const active = activeSection === item.id;
          return <button key={item.id} onClick={() => setActiveSection(item.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm font-semibold rounded-lg transition-colors ${active ? 'bg-[#0F172A] text-white' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1798" data-mp="1n9dz4jy00ypc">
                <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-slate-400'}`} data-id="element-1799" data-mp="2616u2jwmotzp" />
                {item.label}
              </button>;
        })}
        </nav>
      </div>

      {/* Content area */}
      <div className="flex-1 min-w-0" data-id="element-1800" data-mp="3hhrlt6o8rl3m">
        {activeSection === 'categories' && renderCategories()}
        {activeSection === 'reseaux' && renderReseaux()}
        {activeSection === 'modeles' && renderModeles()}
        {activeSection === 'identite' && renderIdentite()}
      </div>
    </div>;
}